import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authAPI, propertyAPI } from '../services/api';
import { storageUrl, normalizeMediaUrl } from '../utils/apiBase';

const LandReporterDashboard = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [myProperties, setMyProperties] = useState([]);
  const [myTransactions, setMyTransactions] = useState([]);
  const [myDistributions, setMyDistributions] = useState([]);
  const [referralInfo, setReferralInfo] = useState(null);
  const [statistics, setStatistics] = useState({});
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const getImageSrc = useCallback(
    (image) => {
      if (!image) return null;
      if (image.image_url) {
        return normalizeMediaUrl(image.image_url);
      }
      if (image.url) {
        return normalizeMediaUrl(image.url);
      }
      let path = image.file_path || image.path;
      if (!path) return null;
      if (path.startsWith('http')) return normalizeMediaUrl(path);
      path = path.replace(/^public\//, '').replace(/^\/?storage\//, '');
      return storageUrl(path);
    },
    []
  );

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/role-selection');
        return;
      }

      console.log('🔧 Using authAPI for /auth/me');
      const response = await authAPI.get('/auth/me');
      console.log('✅ Auth response:', response.data);
      const data = response.data;
      
      if (data.success && data.data.role === 'land_reporter') {
        setUser(data.data);
      } else {
        navigate('/');
        return;
      }
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/role-selection');
      return;
    }
    setLoading(false);
  }, [navigate]);

  const calculateStatistics = useCallback(() => {
    const totalEarnings = user?.earnings || 0;
    const pendingPayout = user?.pending_payout || 0;
    const totalTransactions = myTransactions.length;
    const approvedProperties = myProperties.filter(p => p.status === 'approved').length;
    
    setStatistics({
      totalEarnings,
      pendingPayout,
      totalTransactions,
      approvedProperties,
      totalProperties: myProperties.length,
      pendingProperties: myProperties.filter(p => p.status === 'pending').length,
    });
  }, [user, myTransactions, myProperties]);

  const loadData = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      console.log('🔧 Using propertyAPI for /properties/my-properties');
      // Load user's properties
      const propertiesResponse = await propertyAPI.get('/properties/my-properties');
      console.log('✅ Properties response:', propertiesResponse.data);
      setMyProperties(propertiesResponse.data.data || []);

      // Load revenue transactions
      const transactionsResponse = await propertyAPI.get('/revenue/my-transactions');
      setMyTransactions(transactionsResponse.data.data?.data || []);

      // Load monthly distributions (paginated)
      const distributionsResponse = await propertyAPI.get('/revenue/my-distributions');
      setMyDistributions(distributionsResponse.data?.data?.data || []);

      // Load referral info (code + link + stats)
      const referralsResponse = await authAPI.get('/referrals/me');
      if (referralsResponse.data?.success) {
        setReferralInfo(referralsResponse.data.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  }, []);

  const handleMarkSold = useCallback(
    async (property) => {
      if (!property) return;
      const confirmed = window.confirm(
        t('reporterDashboard.confirmMarkSold', { title: property.title })
      );
      if (!confirmed) return;

      try {
        const response = await propertyAPI.post(`/properties/${property.id}/mark-sold`);
        const updatedProperty = response.data?.data;
        setMyProperties((prev) =>
          prev.map((p) => (p.id === property.id ? { ...p, ...(updatedProperty || { status: 'sold' }) } : p))
        );
        alert(response.data?.message || t('reporterDashboard.markSoldSuccess'));
      } catch (error) {
        console.error('Failed to mark property as sold:', error);
        const message =
          error.response?.data?.message ||
          t('reporterDashboard.markSoldFailed');
        alert(message);
      }
    },
    [setMyProperties, t]
  );

  useEffect(() => {
    checkAuth();
    loadData();
  }, [checkAuth, loadData]);

  // Recalculate statistics whenever inputs change, decoupled from loadData to avoid loops
  useEffect(() => {
    calculateStatistics();
  }, [calculateStatistics]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg text-gray-700">{t('reporterDashboard.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow overflow-x-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 py-6 lg:flex-row lg:justify-between lg:items-start lg:gap-6 min-w-0">
            <div className="min-w-0 flex-1 lg:min-w-min">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 break-words">
                {t('reporterDashboard.title')}
              </h1>
              <p className="text-sm text-gray-600 mt-1">{t('reporterDashboard.welcomeBack', { name: user?.name || '' })}</p>
            </div>
            <div className="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end sm:gap-x-4 sm:gap-y-2 lg:flex-nowrap">
              <div className="flex min-w-0 flex-1 justify-between gap-3 sm:flex-initial sm:justify-end">
                <div className="min-w-0 text-left sm:text-right">
                  <div className="text-xs text-gray-600 sm:text-sm">{t('reporterDashboard.totalEarnings')}</div>
                  <div className="text-lg font-bold tabular-nums text-green-600 sm:text-xl">
                    ${user?.earnings || 0}
                  </div>
                </div>
                <div className="min-w-0 text-left sm:text-right">
                  <div className="text-xs text-gray-600 sm:text-sm">{t('reporterDashboard.pendingPayout')}</div>
                  <div className="text-lg font-bold tabular-nums text-orange-600 sm:text-xl">
                    ${user?.pending_payout || 0}
                  </div>
                </div>
              </div>
              <div className="grid w-full min-w-0 grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:justify-end">
                <button
                  type="button"
                  onClick={() => navigate('/report')}
                  className="inline-flex min-h-[44px] items-center justify-center gap-1 rounded-md bg-green-600 px-2 py-2 text-xs font-medium text-white hover:bg-green-700 sm:min-h-0 sm:px-4 sm:text-sm"
                >
                  <span aria-hidden>🏗️</span>
                  <span className="truncate">{t('navigation.report')}</span>
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-md bg-red-600 px-2 py-2 text-xs font-medium text-white hover:bg-red-700 sm:min-h-0 sm:px-4 sm:text-sm"
                >
                  <span className="truncate">{t('navigation.logout')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="-mx-1 flex flex-wrap gap-x-2 gap-y-1 overflow-x-auto pb-px sm:gap-x-6">
            {[
              { id: 'overview', label: t('reporterDashboard.tabs.overview') },
              { id: 'properties', label: t('reporterDashboard.tabs.properties') },
              { id: 'transactions', label: t('reporterDashboard.tabs.transactions') },
              { id: 'distributions', label: t('reporterDashboard.tabs.distributions') },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`shrink-0 whitespace-nowrap rounded-t px-2 py-3 text-left text-sm font-medium border-b-2 sm:px-1 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">$</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">{t('reporterDashboard.totalEarnings')}</dt>
                        <dd className="text-lg font-medium text-gray-900">${statistics.totalEarnings}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">⏳</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">{t('reporterDashboard.pendingPayout')}</dt>
                        <dd className="text-lg font-medium text-gray-900">${statistics.pendingPayout}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">🏠</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">{t('reporterDashboard.activeProperties')}</dt>
                        <dd className="text-lg font-medium text-gray-900">{statistics.approvedProperties}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                        <span className="text-white font-bold">💳</span>
                      </div>
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-sm font-medium text-gray-500 truncate">{t('reporterDashboard.revenueTransactions')}</dt>
                        <dd className="text-lg font-medium text-gray-900">{statistics.totalTransactions}</dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Referral Link */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {t('reporterDashboard.referrals.title')}
                  </h3>
                  {referralInfo?.referral_code && (
                    <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                      {referralInfo.referral_code}
                    </span>
                  )}
                </div>

                <p className="text-sm text-gray-600 mb-4">{t('reporterDashboard.referrals.subtitle')}</p>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <input
                    type="text"
                    readOnly
                    value={referralInfo?.referral_url || ''}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-800"
                    placeholder={t('reporterDashboard.referrals.loadingLink')}
                  />
                  <button
                    type="button"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                    onClick={async () => {
                      const url = referralInfo?.referral_url;
                      if (!url) return;
                      try {
                        await navigator.clipboard.writeText(url);
                        alert(t('reporterDashboard.referrals.copied'));
                      } catch (_e) {
                        window.prompt(t('reporterDashboard.referrals.copyPrompt'), url);
                      }
                    }}
                    disabled={!referralInfo?.referral_url}
                  >
                    {t('reporterDashboard.referrals.copyButton')}
                  </button>
                </div>

                {referralInfo?.stats && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
                    <div className="bg-gray-50 rounded-md p-4">
                      <div className="text-sm text-gray-600">{t('reporterDashboard.referrals.thisMonth')}</div>
                      <div className="text-xl font-bold text-gray-900">{referralInfo.stats.this_month_earned}</div>
                    </div>
                    <div className="bg-gray-50 rounded-md p-4">
                      <div className="text-sm text-gray-600">{t('reporterDashboard.referrals.lastMonth')}</div>
                      <div className="text-xl font-bold text-gray-900">{referralInfo.stats.last_month_earned}</div>
                    </div>
                    <div className="bg-gray-50 rounded-md p-4">
                      <div className="text-sm text-gray-600">{t('reporterDashboard.referrals.total')}</div>
                      <div className="text-xl font-bold text-gray-900">{referralInfo.stats.total_earned}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{t('reporterDashboard.recentActivity')}</h3>
                <div className="space-y-4">
                  {myTransactions.slice(0, 5).map((transaction) => (
                    <div key={transaction.id} className="flex items-center justify-between border-b pb-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {t('reporterDashboard.paymentReceivedFor', { title: transaction.property?.title || '' })}
                        </p>
                        <p className="text-sm text-gray-500">
                          {t('reporterDashboard.fromDate', {
                            name: transaction.payer?.name || '',
                            date: new Date(transaction.created_at).toLocaleDateString(i18n.language || undefined),
                          })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-green-600">+${transaction.amount}</p>
                        <p className="text-xs text-gray-500">{t('reporterDashboard.contactViewPayment')}</p>
                      </div>
                    </div>
                  ))}
                  {myTransactions.length === 0 && (
                    <p className="text-sm text-gray-500">{t('reporterDashboard.noTransactionsYet')}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'properties' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{t('reporterDashboard.myProperties')}</h3>
              <div className="space-y-4">
                {myProperties.map((property) => (
                  <PropertyCard
                    key={property.id}
                    property={property}
                    getImageSrc={getImageSrc}
                    onMarkSold={handleMarkSold}
                  />
                ))}
                {myProperties.length === 0 && (
                  <p className="text-sm text-gray-500">
                    {t('reporterDashboard.noPropertiesYet')}{' '}
                    <a href="/land-search" className="text-blue-600 hover:underline">
                      {t('reporterDashboard.addFirstProperty')}
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transactions' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{t('reporterDashboard.revenueTransactions')}</h3>
              <div className="space-y-4">
                {myTransactions.map((transaction) => (
                  <TransactionCard key={transaction.id} transaction={transaction} />
                ))}
                {myTransactions.length === 0 && (
                  <p className="text-sm text-gray-500">{t('reporterDashboard.noTransactionsYet')}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'distributions' && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">{t('reporterDashboard.monthlyDistributions')}</h3>
              <div className="space-y-4">
                {(Array.isArray(myDistributions) ? myDistributions : []).map((distribution) => (
                  <DistributionCard key={distribution.id} distribution={distribution} />
                ))}
                {(Array.isArray(myDistributions) ? myDistributions.length === 0 : true) && (
                  <p className="text-sm text-gray-500">{t('reporterDashboard.noDistributionsYet')}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Property Card Component
const PropertyCard = ({ property, getImageSrc, onMarkSold }) => {
  const { t, i18n } = useTranslation();
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'sold': return 'bg-gray-200 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const primaryImage =
    property.images?.find((img) => img?.is_primary && img?.file_type !== 'video') ||
    property.images?.find((img) => img?.file_type !== 'video') ||
    property.images?.[0] ||
    null;
  const primaryImageSrc = getImageSrc ? getImageSrc(primaryImage) : null;
  const isPrimaryVideo = primaryImage?.file_type === 'video';

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-shrink-0">
          {primaryImageSrc ? (
            isPrimaryVideo ? (
              <video
                src={primaryImageSrc}
                className="w-24 h-24 rounded-lg object-cover"
                controls
              />
            ) : (
              <img
                src={primaryImageSrc}
                alt={property.title}
                className="w-24 h-24 rounded-lg object-cover"
              />
            )
          ) : (
            <div className="w-24 h-24 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400 text-xs">
              {t('reporterDashboard.noMedia')}
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-medium text-gray-900">{property.title}</h4>
              <p className="text-sm text-gray-600">{property.description}</p>
              <p className="text-sm text-gray-500">{property.address}{property.city ? `, ${property.city}` : ''}</p>
              <p className="text-sm text-gray-500">${property.price} • {property.property_type}</p>
              <p className="text-xs text-gray-400">
                {t('reporterDashboard.created')}: {new Date(property.created_at).toLocaleDateString(i18n.language || undefined)}
              </p>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(property.status)}`}>
                {property.status}
              </span>
              {/* Commission removed – rewards are monthly pool based */}
            </div>
          </div>
          {property.images?.length > 1 && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {property.images.slice(0, 3).map((image, index) => {
                const thumbSrc = getImageSrc ? getImageSrc(image) : null;
                if (!thumbSrc) {
                  return (
                    <div key={index} className="w-full h-16 bg-gray-200 flex items-center justify-center text-gray-400 text-xs rounded">
                      {t('reporterDashboard.noMedia')}
                    </div>
                  );
                }
                const isVideo = image.file_type === 'video';
                return (
                  <div key={index} className="relative">
                    {isVideo ? (
                      <video
                        src={thumbSrc}
                        className="w-full h-16 object-cover rounded"
                        controls
                      />
                    ) : (
                      <img
                        src={thumbSrc}
                        alt={`${property.title} ${index + 1}`}
                        className="w-full h-16 object-cover rounded"
                      />
                    )}
                    {isVideo && (
                      <span className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-[10px] px-1 rounded">
                        {t('reporterDashboard.video')}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {property.status === 'approved' && typeof onMarkSold === 'function' && (
          <button
            type="button"
            onClick={() => onMarkSold(property)}
            className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
          >
            {t('reporterDashboard.propertySold')}
          </button>
        )}
        {property.status === 'sold' && (
          <span className="text-xs text-gray-500">
            {t('reporterDashboard.soldNote')}
          </span>
        )}
      </div>
    </div>
  );
};

// Transaction Card Component
const TransactionCard = ({ transaction }) => {
  const { t, i18n } = useTranslation();
  const earned = 0; // Reporter earnings for a single contact-view are paid via monthly pool
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">{transaction.property?.title}</h4>
          <p className="text-sm text-gray-600">{t('reporterDashboard.paidBy', { name: transaction.payer?.name || '' })}</p>
          <p className="text-sm text-gray-500">
            {t('reporterDashboard.date')}: {new Date(transaction.created_at).toLocaleDateString(i18n.language || undefined)}
          </p>
          <p className="text-sm text-gray-500">{t('reporterDashboard.status')}: {transaction.status}</p>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-green-600">
            {t('reporterDashboard.yourEarnings', { amount: earned.toFixed(2) })}
          </div>
          <div className="text-xs text-gray-500">{t('reporterDashboard.monthlyPoolNote')}</div>
        </div>
      </div>
    </div>
  );
};

// Distribution Card Component
const DistributionCard = ({ distribution }) => {
  const { t, i18n } = useTranslation();
  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h4 className="font-medium text-gray-900">
            {t('reporterDashboard.distributionTitle', {
              year: distribution.monthly_distribution.year,
              month: String(distribution.monthly_distribution.month).padStart(2, '0'),
            })}
          </h4>
          <p className="text-sm text-gray-600">
            {t('reporterDashboard.yourShare', {
              share: distribution.share_percentage,
              count: distribution.active_posts_count,
            })}
          </p>
          <p className="text-sm text-gray-500">
            {t('reporterDashboard.totalPool', { amount: distribution.monthly_distribution.total_pool_amount })}
          </p>
          <p className="text-sm text-gray-500">
            {t('reporterDashboard.amountPerPost', { amount: distribution.monthly_distribution.amount_per_post })}
          </p>
          {distribution.paid_at && (
            <p className="text-xs text-gray-400">
              {t('reporterDashboard.paid')}: {new Date(distribution.paid_at).toLocaleDateString(i18n.language || undefined)}
            </p>
          )}
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-green-600">${distribution.amount_earned}</div>
          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(distribution.status)}`}>
            {distribution.status}
          </span>
        </div>
      </div>
    </div>
  );
};

export default LandReporterDashboard;

